import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;
  #confirmTimer = 0;
  #confirmOriginal = null;
  #confirmButtonEl = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
      } else {
        // Stay on the product page and slide in the frosted cart drawer; updateCart has already refreshed its partials.
        this.#confirmButton(submitter);
        this.#openCartDrawer();
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #celebrateAdd(submitter) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#confirmButton(submitter);
    if (reduceMotion) return;

    // The header badge partial is swapped after the cart refresh; resolve the cart trigger at flight time.
    const target = document.getElementById('header-cart-trigger');
    const source = document.querySelector('[data-product-gallery] img');
    if (target && source instanceof HTMLImageElement && source.currentSrc) {
      const from = source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      const width = 72;
      const height = 108;
      const startX = from.left + from.width / 2 - width / 2;
      const startY = Math.max(16, Math.min(from.top + from.height / 2, window.innerHeight - 16)) - height / 2;
      const endX = to.left + to.width / 2 - width / 2;
      const endY = to.top + to.height / 2 - height / 2;

      const flyer = document.createElement('img');
      flyer.src = source.currentSrc;
      flyer.alt = '';
      flyer.setAttribute('aria-hidden', 'true');
      Object.assign(flyer.style, {
        position: 'fixed', left: '0', top: '0', width: `${width}px`, height: `${height}px`,
        objectFit: 'cover', zIndex: '2147483646', pointerEvents: 'none',
        boxShadow: '0 12px 32px -12px rgb(38 38 38 / 0.45)',
      });
      document.body.append(flyer);

      const flight = flyer.animate(
        [
          { transform: `translate(${startX}px, ${startY}px) scale(1)`, opacity: 1 },
          { transform: `translate(${(startX + endX) / 2}px, ${Math.min(startY, endY) - 60}px) scale(0.7)`, opacity: 1, offset: 0.55 },
          { transform: `translate(${endX}px, ${endY}px) scale(0.15)`, opacity: 0.4 },
        ],
        { duration: 750, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' },
      );
      flight.finished.catch(() => {}).finally(() => {
        flyer.remove();
        this.#bumpCart();
      });
    } else {
      this.#bumpCart();
    }
  }

  #openCartDrawer() {
    const dialog = document.getElementById('cart-drawer');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.open) dialog.showModal();
    if (dialog.open) dialog.dispatchEvent(new CustomEvent('s-cart:opened', { bubbles: true, detail: { dialogId: dialog.id } }));
  }

  #bumpCart() {
    const icon = document.querySelector('#header-cart-trigger > span') || document.getElementById('header-cart-trigger');
    icon?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.3)' }, { transform: 'scale(0.95)' }, { transform: 'scale(1)' }],
      { duration: 450, easing: 'ease-out' },
    );
  }

  #confirmButton(submitter) {
    if (!(submitter instanceof HTMLElement)) return;
    const added = this.dataset.addedLabel;
    if (!added) return;
    clearTimeout(this.#confirmTimer);
    if (this.#confirmOriginal == null || !submitter.isConnected) this.#confirmOriginal = submitter.innerHTML;
    this.#confirmButtonEl = submitter;
    submitter.textContent = added;
    this.#confirmTimer = setTimeout(() => {
      if (this.#confirmButtonEl?.isConnected && this.#confirmOriginal != null) this.#confirmButtonEl.innerHTML = this.#confirmOriginal;
      this.#confirmOriginal = null;
      this.#confirmButtonEl = null;
    }, 1800);
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
